import useConversationHistory from '../offline/useConversationHistory';
import { useOfflineSession } from '../offline/OfflineSessionContext';
import InterfaceLanguageSelect from './InterfaceLanguageSelect';
import { projectAuthHeaders } from '../projectAuth';
import { agentForDomain, domainForAgent } from '../chatDomains';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiBarChart2, FiFolder, FiLogOut, FiMessageSquare, FiPlus, FiUser } from 'react-icons/fi';
import WorkspaceDialog from './WorkspaceDialog';
import { FaWhatsapp } from 'react-icons/fa';
import { useWhatsAppAccess } from './WhatsAppAccess';
import useIsAdmin from '../admin/useIsAdmin';
import { BACKEND_MAIN_URL } from '../config';
import { cacheProjectList, listProjects } from '../offline/projectRepository';

export const conversationAgent = (conversation) => {
  if (conversation?.agent_id) return conversation.agent_id;
  const message = conversation?.messages?.find((item) => item.agent_id || item.agentId);
  return message?.agent_id || message?.agentId || 'default';
};

export default function MobileSidebar({ isOpen, onClose, onLogout, children, activeDomain = 'agri' }) {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const location = useLocation();
  const { openWhatsApp } = useWhatsAppAccess();
  const { offlineReadOnly, chatBackendUnavailable, sessionExpired, reconnect, userId } = useOfflineSession();
  const [notice, setNotice] = useState(null);
  const admin = useIsAdmin();
  const [section, setSection] = useState('discussions');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const history = useConversationHistory({ enabled: isOpen && section === 'discussions', refreshTrigger: retry });
  useEffect(() => { setSearch(''); setShowAll(false); }, [isOpen, section]);
  useEffect(() => { setNotice(null); }, [isOpen, offlineReadOnly]);
  useEffect(() => { if (isOpen) setSection(location.pathname.startsWith('/projects') ? 'projects' : 'discussions'); }, [isOpen, location.pathname]);
  useEffect(() => {
    if (!isOpen || section !== 'projects' || !userId) return;
    let active = true;
    setLoading(true); setError(false); setItems([]);
    const load = async () => {
      try {
        const cached = await listProjects(userId);
        if (active && cached.length) { setItems(cached); setLoading(false); }
        if (offlineReadOnly || chatBackendUnavailable || navigator.onLine === false) return;
        const { data } = await axios.get(`${BACKEND_MAIN_URL}/api/projects`, { headers: projectAuthHeaders() });
        await cacheProjectList(userId, data.projects || []);
        if (active) setItems(await listProjects(userId));
      } catch { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [isOpen, section, retry, offlineReadOnly, chatBackendUnavailable, userId]);
  const displayItems = section === 'discussions'
    ? history.conversations.filter((item) => domainForAgent(conversationAgent(item)) === activeDomain)
    : items;
  const displayLoading = section === 'discussions' ? history.isLoading && !displayItems.length : loading;
  const displayError = section === 'discussions' ? history.error && !displayItems.length : error && !displayItems.length;
  const go = (path, options) => { onClose(); navigate(path, options); };
  const openOnlinePage = (path) => {
    if (offlineReadOnly) { setNotice('session'); return; }
    go(path);
  };
  const openWhatsAppPage = () => {
    if (offlineReadOnly) { setNotice('session'); return; }
    if (chatBackendUnavailable) { setNotice('whatsapp'); return; }
    onClose(); openWhatsApp();
  };
  const matchingItems = displayItems.filter((item) => !search.trim() || (item.name || item.title || '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const sortedItems = section === 'projects' ? matchingItems : [...matchingItems].sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  const visibleItems = section === 'projects' || showAll || search.trim() ? sortedItems : sortedItems.slice(0, 6);
  return <WorkspaceDialog open={isOpen} onClose={onClose} title="UpyaChat" className="ws-navigation">
    <div className="ws-navigation-body">
      <button type="button" className="ws-primary" onClick={() => go('/chat', { state: { conversation: null, timestamp: Date.now(), agentId: agentForDomain(activeDomain) } })}><FiPlus />{t('newChat')}</button>
      <div className="ws-segmented" aria-label={t('menu')}>
        <button type="button" aria-pressed={section === 'discussions'} onClick={() => setSection('discussions')}><FiMessageSquare />{t('discussions')}</button>
        <button type="button" aria-pressed={section === 'projects'} onClick={() => setSection('projects')}><FiFolder />{t('projects')}</button>
      </div>
      {section === 'projects' && <button type="button" className="ws-text-button" onClick={() => go('/projects')}>{t('allProjects')}<FiArrowRight /></button>}
      {section === 'discussions' && <>
        <label className="ws-history-search"><span className="ws-sr-only">{t('searchConversations')}</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('searchConversations')} /></label>
        <h3 className="ws-history-heading">{t(showAll ? 'allConversations' : 'recentConversations')}</h3>
      </>}
      <div className="ws-navigation-list">
        {displayLoading ? <p role="status">{t('loading')}</p> : displayError ? <div role="alert"><p>{t('loadError')}</p><button className="ws-secondary" onClick={() => setRetry((value) => value + 1)}>{t('retry')}</button></div> : visibleItems.length === 0 ? <p className="ws-muted">{t(search.trim() ? 'noSearchResults' : section === 'projects' ? 'emptyProjects' : 'noHistory')}</p> :
          visibleItems.map((item) => <button className="ws-history-link" key={item.id} type="button" onClick={() => section === 'projects' ? go(`/projects/${item.id}`) : go('/chat', { state: { conversation: item, timestamp: Date.now(), agentId: conversationAgent(item) } })}>
            {section === 'projects' ? <FiFolder /> : <FiMessageSquare />}<span>{item.name || item.title || t('untitled')}{section === 'discussions' && <small className="ws-history-domain">{t(`domain_${domainForAgent(conversationAgent(item))}`)}</small>}</span><FiArrowRight />
          </button>)}
      </div>
      {section === 'discussions' && !search.trim() && sortedItems.length > 6 && <button type="button" className="ws-text-button" onClick={() => setShowAll((value) => !value)}>{t(showAll ? 'recentConversations' : 'allConversations')}</button>}
      <div className="ws-navigation-footer">
        <button className="ws-text-button" type="button" aria-haspopup="dialog" onClick={openWhatsAppPage}><FaWhatsapp aria-hidden="true" />WhatsApp</button>
        {admin && <button className="ws-text-button" type="button" onClick={() => openOnlinePage('/admin')}><FiBarChart2 />Admin</button>}
        <button className="ws-text-button" type="button" onClick={() => openOnlinePage('/account')}><FiUser />{t('account')}</button>
        <button className="ws-text-button" type="button" onClick={() => openOnlinePage('/upgrade')}>{t('subscription')}</button>
        {notice && <WorkspaceDialog title={notice === 'whatsapp' ? 'WhatsApp' : t('account')} onClose={() => setNotice(null)}><div role="alert">
          <p>{t(notice === 'whatsapp' ? 'whatsappLocalUnavailable' : sessionExpired ? 'offlineSessionExpired' : 'offlineSessionReader')}</p>
          {notice === 'session' && <button className="ws-secondary" type="button" onClick={() => { if (sessionExpired) go('/login'); else { onClose(); reconnect(); } }}>{t(sessionExpired ? 'offlineSessionSignIn' : 'offlineSessionRetry')}</button>}
        </div></WorkspaceDialog>}
        {children}
        <InterfaceLanguageSelect />
        <div className="ws-navigation-bottom"><button type="button" className="ws-text-button" onClick={() => go('/terms')}>{t('terms')}</button><button type="button" className="ws-icon-button" title={t('logout')} aria-label={t('logout')} onClick={() => { onClose(); onLogout?.(); }}><FiLogOut /></button></div>
      </div>
    </div>
  </WorkspaceDialog>;
}
